import { Fragment, useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";

import ButtonDefault from "components/Buttons/ButtonDefault";
import ButtonPrimary from "components/Buttons/ButtonPrimary";

import SpinnerCentered from "components/Loader/SpinnerCentered";
import { saveCompany } from "services/http/companies";
import CompanyForm from "components/CompanyForm/CompanyForm";
import CompanyAreaForm from "components/CompanyAreaForm/CompanyAreaForm";
import { companyContactsSchema } from "~/validation/contactFields";

const useStyles = makeStyles((_theme) => ({
  content: { padding: 48 },
  title: { paddingBottom: 32 },
}));

function formatCompanyEditValues(_values) {
  const secondary_cities = (_values.secondary_cities ?? []).map((city) => ({
    value: city.id,
    label: city.name,
  }));

  const kela_areas = (_values.kela_areas ?? []).map((kela_area) => ({
    value: kela_area.id,
    label: kela_area.name,
  }));

  return {
    ..._values,
    secondary_cities,
    kela_areas,
  };
}

function formatSaveErrorMessage(errors) {
  const data = errors?.data;
  if (!data || typeof data !== "object") {
    return null;
  }
  const messages = Object.values(data).flatMap((value) =>
    Array.isArray(value) ? value : [String(value)],
  );
  return messages.length > 0 ? messages.join(" ") : null;
}

const CompanyEdit = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const { users, getMyCompany } = props;

  const [values, setValues] = useState({
    receiverLanguage: null,
    type: 1,
    send_phone: false,
    send_email: false,
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleGetMyCompany = useCallback(() => {
    setLoading(true);
    getMyCompany().then(
      (response) => {
        try {
          setValues(formatCompanyEditValues(response));
        } catch (_error) {
          enqueueSnackbar(t("Yrityksen tietojen lataus epäonnistui"), {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            autoHideDuration: 4000,
          });
        } finally {
          setLoading(false);
        }
      },
      (_errors) => {
        setLoading(false);
      },
    );
  }, [getMyCompany, enqueueSnackbar, t]);

  useEffect(() => {
    handleGetMyCompany();
  }, [handleGetMyCompany]);

  const updateValues = (updated_values) => {
    if (updated_values == null) {
      return;
    }

    setValues((prev) => ({ ...prev, ...updated_values }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!values?.checked) {
      try {
        companyContactsSchema().validateSync({
          contact_email: values.contact_email,
          contact_phone: values.contact_phone,
        });
      } catch (e) {
        enqueueSnackbar(e?.message ?? t("Tarkista yhteystiedot"), {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 4000,
        });
        return;
      }
    }

    setSaving(true);

    const secondary_cities = (values.secondary_cities ?? []).map((city) => ({
      id: city.value,
    }));

    const kela_areas = (values.kela_areas ?? []).map((kela_area) => ({
      id: kela_area.value,
    }));

    const main_city =
      values.main_city && values?.main_city?.id > -1 ? values.main_city : null;

    const request = {
      data: values?.checked
        ? {
            id: values.id,
            main_city,
            secondary_cities,
            kela_areas,
          }
        : {
            id: values.id,
            name: values.name,
            business_id: values.business_id,
            contact_email: values.contact_email,
            contact_phone: values.contact_phone,
            main_city,
            secondary_cities,
            kela_areas,
          },
    };

    saveCompany(request).then(
      (_response) => {
        // setValues(formatValues(response));
        handleGetMyCompany();

        enqueueSnackbar("Tallennettu", {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });
        setSaving(false);
      },
      (errors) => {
        enqueueSnackbar(
          formatSaveErrorMessage(errors) ?? t("Tallennus epäonnistui"),
          {
            variant: "error",
            anchorOrigin: { vertical: "bottom", horizontal: "center" },
            autoHideDuration: 4000,
          },
        );
        setSaving(false);
      },
    );
  };

  const handleCancel = () => {
    navigate("/dashboard/start");
  };

  return (
    <Paper className={classes.content} elevation={0} square>
      {/*
        onError={errors => console.log(errors)}
        */}
      <form onSubmit={handleSubmit} noValidate>
        {loading ? (
          <SpinnerCentered />
        ) : (
          <Fragment>
            <Typography variant="h4">{t("Yrityksen perustiedot")}</Typography>

            <CompanyForm values={values} updateValues={updateValues} />

            {!values?.checked ? (
              <Box sx={{ mb: "32px" }}>
                <Grid
                  container
                  direction="row"
                  sx={{ justifyContent: "flex-start", alignItems: "center" }}
                >
                  <Grid size={12}>
                    <ErrorIcon color="error" />
                  </Grid>
                  <Grid size={12}>
                    {t(
                      "Yritys odottaa hyväksyntää. Ota yhteys tarvittaessa tukeen.",
                    )}
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box sx={{ mb: "32px" }}>
                <Grid
                  container
                  direction="row"
                  sx={{ justifyContent: "flex-start", alignItems: "center" }}
                >
                  <Grid size={12}>
                    <InfoIcon color="info" />
                  </Grid>
                  <Grid size={12}>
                    {t(
                      "Ylläpitäjä on hyväksynyt yrityksen perustiedot. Perustietoja voi muuttaa ottamalla yhteyttä ylläpitoon.",
                    )}
                  </Grid>
                </Grid>
              </Box>
            )}

            <Typography variant="h4">
              {t("Yrityksen toiminta-alueet")}
            </Typography>

            <Box sx={{ mb: "32px" }}>
              <CompanyAreaForm values={values} updateValues={updateValues} />
            </Box>
          </Fragment>
        )}

        <Grid size={{ xs: 12 }}>
          <Grid
            container
            direction="row"
            sx={{ justifyContent: "space-between", alignItems: "flex-end" }}
          >
            <Grid size={12}>
              <ButtonDefault onClick={handleCancel}>
                {t("Peruuta")}
              </ButtonDefault>
            </Grid>
            <Grid size={12}>
              {/* !values?.checked ||  */}
              <ButtonPrimary
                loading={saving}
                type="submit"
                disabled={users?.data?.me?.status < 100}
              >
                {t("Tallenna")}
              </ButtonPrimary>
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default CompanyEdit;
